import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useState } from "react";
import Helper from "../Helper";
import PageContentType from "../PageContentType";
import Permission from "../Permission";
import Weather from "../Weather";
import { baseUrlAltSubdomain } from "../components/Helper/Url";
import mapIcons from "../mapIcons";
import PlotAPI from "../plotAPI";
import TeamInfo from "../teamInfo";
import GlobalStoreContext from "./GlobalStoreContext";
import useActiveProjects from "./useActiveProjects";
import useAnyCable from "./useAnyCable";
import useCurrentProjectInfo from "./useCurrentProjectInfo";
import useCurrentTeam from "./useCurrentTeam";
import useProjectInfo from "./useProjectInfo";
import useUIHook from "./useUIHook";
import useUserInfo from "./useUserInfo";
import useWindowSize from "./useWindowSize";

function GlobalStoreProvider(props: { children: React.ReactNode }) {
    const [pageContentType, setPageContentType] = useState<PageContentType | null>(null); // Type of content being shown in the content area
    const [currentViewedTeamInfo, _setCurrentViewedTeamInfo] = useState<TeamInfo | null>(null);
    const [newBroadcastMessage, setNewBroadcastMessage] = useState<boolean>(false);
    const [currentProjectWeather, setCurrentProjectWeather] = useState<null | any>(null);
    const [currentAppVersion, _setCurrentAppVersion] = useState<null | string>(null);

    const {
        activeSidebarKey,
        setActiveSidebarKey,
        collapseSidebar,
        setCollapseSidebar,
        showNotificationsDrawer,
        setShowNotificationsDrawer,
        addTeamToProjectModal,
        setAddTeamToProjectModal,
    } = useUIHook();

    const { windowSize, devicePixelRatio, isMobile } = useWindowSize();

    const { currentProjectToken, setCurrentProjectToken, currentProjectInfo, reloadCurrentProject } =
        useCurrentProjectInfo();

    const { userInfo, setUserInfo, userPic, setUserPic, reloadUserInfo, loggedIn, setUserTheme, usersTeams } =
        useUserInfo({
            pageContentType,
            currentProjectInfo,
        });

    const { currentTeam, currentTeamsProjects, setCurrentTeam, reloadCurrentTeamsProjects } = useCurrentTeam({
        usersTeams,
    });

    const { currentActiveProjects } = useActiveProjects({
        projects: currentTeamsProjects.member_of,
        currentProjectInfo: currentProjectInfo,
        pageContentType: pageContentType,
    });

    const {
        projectInfo,
        mapObjectsByProject,
        mapObjectsForProject,
        refreshMapObjectsByProject,
        mapAreasByProject,
        mapAreasForProject,
        refreshMapAreasByProject,
        equipmentByProject,
        equipmentForProject,
        refreshEquipmentByProject,
        teamsByProject,
        teamsForProject,
        refreshTeamsByProject,
        vendorsByProject,
        vendorsForProject,
        refreshVendorsByProject,
        createDelivery,
        updateDelivery,
        deleteDelivery,
        mapRef,
        mapGeoJson,
        setMapGeoJson,
        mapPopup,
        mapBoxDraw,
        setMapBoxDraw,
        currentMappable,
        setCurrentMappable,
        currentMapObject,
        setCurrentMapObject,
        editCurrentMappable,
        setEditCurrentMappable,
        newMappableType,
        setNewMappableType,
        editCurrentMappablePosition,
        setEditCurrentMappablePosition,
        mapFilterDate,
        setMapFilterDate,
        currentWaypointStep,
        setCurrentWaypointStep,
        mapBounds,
        userLocation,
        setUserLocation,
        geoControlRef,
        waypointGuidance,
        setWaypointGuidance,
        calendarEventsFilterInfo,
        setCalendarEventsFilterInfo,
        refreshCalendarEventsByProject,
        calendarEventsByProject,
        calendarEventsForProject,
        updateCalendarEventInState,
        createCalendarEvent,
        updateCalendarEvent,
        deleteCalendarEvent,
    } = useProjectInfo({
        userInfo: userInfo,
        projects: currentActiveProjects,
    });

    const { cableApp, dashboardNotificationsChannel, mapChannel } = useAnyCable({
        projectInfo: currentProjectInfo,
        reloadCurrentProject: reloadCurrentProject,
        setNewBroadcastMessage: setNewBroadcastMessage,
        updateCalendarEventInState: updateCalendarEventInState,
        calendarEventsByProject: calendarEventsByProject,
    });

    const darkMode = React.useMemo(() => {
        if (!userInfo) {
            return false;
        }

        return userInfo.user_preferences?.theme === "dark";
    }, [userInfo]);

    /**
     * Get current weather for a project  jobsite
     * @returns weather if found, else null.
     */
    const getCurrentProjectWeather = useCallback((projectToken: string): void => {
        Weather.getWeatherInformation(projectToken).then((response) => {
            if (response?.provider) {
                setCurrentProjectWeather(response);
            } else {
                Sentry.captureMessage("Error getting weather information" + response);
            }
        });
    }, []);

    // Gets all weather for the current project.
    useEffect(() => {
        if (!currentProjectInfo?.unique_token) {
            return;
        }

        getCurrentProjectWeather(currentProjectInfo.unique_token);
    }, [currentProjectInfo, getCurrentProjectWeather]);

    const setCurrentAppVersion = useCallback(() => {
        const url = baseUrlAltSubdomain("app") + "/version.txt";
        Helper.plotFetch(url)
            .then((response) => response.text())
            .then((text) => _setCurrentAppVersion(text));
    }, []);

    useEffect(() => {
        setCurrentAppVersion();
    }, [setCurrentAppVersion]);

    useEffect(() => {
        if (
            pageContentType === PageContentType.TEAM ||
            pageContentType == PageContentType.DELIVERY ||
            pageContentType == PageContentType.PROJECT
        ) {
            PlotAPI.getCurrentSubdomainTeam().then((response) => {
                _setCurrentViewedTeamInfo(response ? new TeamInfo(response) : null);
            });
        }
    }, [pageContentType]);

    const reloadCurrentViewedTeamInfo = useCallback((): void => {
        PlotAPI.getCurrentSubdomainTeam().then((response) => {
            _setCurrentViewedTeamInfo(response ? new TeamInfo(response) : null);
        });
    }, []);

    const userHasPermission = useCallback(
        (permission: Permission, team: number | undefined, project: number | null | undefined) => {
            let loadedProjectsIfNeeded = false;
            if (project == null) {
                // Do need to ensure loaded
                loadedProjectsIfNeeded = true;
            } else if (currentProjectToken && project == currentProjectInfo?.id && currentProjectInfo) {
                // We want the viewed project and it is loaded
                loadedProjectsIfNeeded = true;
            } else if (currentTeamsProjects) {
                // We want any other project which should only be current users teams projects
                loadedProjectsIfNeeded = true;
            }

            let loadedTeamsIfNeeded = false;
            if (team == null) {
                // Checking explict null in case of team id 0
                loadedTeamsIfNeeded = true;
            } else if ((currentTeam && currentTeam.id == team) || currentTeam == null) {
                loadedTeamsIfNeeded = true;
            } else if (currentViewedTeamInfo && currentViewedTeamInfo.id == team) {
                loadedTeamsIfNeeded = true;
            }

            if (userInfo && loadedProjectsIfNeeded && loadedTeamsIfNeeded) {
                return userInfo.permission(permission, team, project);
            }
            return false;
        },
        [currentProjectInfo, currentProjectToken, currentTeam, currentTeamsProjects, currentViewedTeamInfo, userInfo]
    );

    return (
        <GlobalStoreContext.Provider
            value={{
                userInfo,
                setUserInfo,
                userPic,
                setUserPic,
                reloadUserInfo,
                loggedIn,
                usersTeams,
                currentTeam,
                setCurrentTeam,
                reloadCurrentTeamsProjects,
                currentTeamsProjects,
                pageContentType,
                setPageContentType,
                currentProjectToken,
                setCurrentProjectToken,
                currentProjectInfo,
                currentViewedTeamInfo,
                reloadCurrentProject,
                reloadCurrentViewedTeamInfo,
                windowSize,
                devicePixelRatio,
                isMobile,
                cableApp,
                mapIcons,
                currentProjectWeather,
                getCurrentProjectWeather,
                userHasPermission,
                currentAppVersion,
                setCurrentAppVersion,
                dashboardNotificationsChannel,
                newBroadcastMessage,
                setNewBroadcastMessage,
                mapChannel: mapChannel,
                setUserTheme,
                activeSidebarKey,
                setActiveSidebarKey,
                collapseSidebar,
                setCollapseSidebar,
                showNotificationsDrawer,
                setShowNotificationsDrawer,
                addTeamToProjectModal,
                setAddTeamToProjectModal,
                projectInfo,
                mapObjectsByProject,
                mapObjectsForProject,
                refreshMapObjectsByProject,
                mapAreasByProject,
                mapAreasForProject,
                refreshMapAreasByProject,
                equipmentByProject,
                equipmentForProject,
                refreshEquipmentByProject,
                teamsByProject,
                teamsForProject,
                refreshTeamsByProject,
                vendorsByProject,
                vendorsForProject,
                refreshVendorsByProject,
                createDelivery,
                updateDelivery,
                deleteDelivery,
                mapRef,
                mapGeoJson,
                setMapGeoJson,
                mapPopup,
                mapBoxDraw,
                setMapBoxDraw,
                currentMappable,
                setCurrentMappable,
                currentMapObject,
                setCurrentMapObject,
                editCurrentMappable,
                newMappableType,
                setNewMappableType,
                setEditCurrentMappable,
                editCurrentMappablePosition,
                setEditCurrentMappablePosition,
                mapFilterDate,
                setMapFilterDate,
                currentWaypointStep,
                setCurrentWaypointStep,
                mapBounds,
                darkMode,
                userLocation,
                setUserLocation,
                geoControlRef,
                waypointGuidance,
                setWaypointGuidance,
                calendarEventsFilterInfo,
                setCalendarEventsFilterInfo,
                refreshCalendarEventsByProject,
                calendarEventsByProject,
                calendarEventsForProject,
                createCalendarEvent,
                updateCalendarEvent,
                deleteCalendarEvent,
            }}
        >
            {props.children}
        </GlobalStoreContext.Provider>
    );
}

export default GlobalStoreProvider;
